import React, { useEffect, useRef } from 'react';
import { prepareWithSegments } from '@chenglou/pretext';

const FONT_SIZE = 14;
const LINE_HEIGHT = 17;
const PROP_FAMILY = 'var(--font-geek, "Fira Code", monospace)';
const CHARSET = " .,:;!+-=*#@%&abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
const WEIGHTS = [300, 500, 800];
const FONT_STYLES = ["normal", "italic"];
const MAX_COLS = 200;
const MAX_ROWS = 80;

export default function LoginFluidSmoke() {
    const containerRef = useRef<HTMLDivElement>(null);
    const smokeRootRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const container = containerRef.current;
        const smokeRoot = smokeRootRef.current;
        if (!container) return;

        let animationFrameId: number;
        let resizeTimer: NodeJS.Timeout;

        // Hidden canvas to measure exact visual brightness of characters
        const bCvs = document.createElement("canvas");
        bCvs.width = bCvs.height = 28;
        const bCtx = bCvs.getContext("2d", { willReadFrequently: true });

        function estimateBrightness(ch: string, font: string) {
            if (!bCtx) return 0;
            bCtx.clearRect(0, 0, 28, 28);
            bCtx.font = font;
            bCtx.fillStyle = "#fff";
            bCtx.textBaseline = "middle";
            bCtx.fillText(ch, 1, 14);
            const d = bCtx.getImageData(0, 0, 28, 28).data;
            let sum = 0;
            for (let i = 3; i < d.length; i += 4) sum += d[i];
            return sum / (255 * 784);
        }

        const palette: any[] = [];
        
        // 1. Precalculate palette using pretext natively
        for (const style of FONT_STYLES) {
            for (const weight of WEIGHTS) {
                const font = `${style === "italic" ? "italic " : ""}${weight} ${FONT_SIZE}px ${PROP_FAMILY}`;
                for (const ch of CHARSET) {
                    if (ch === " ") continue;
                    try {
                        // We use pretext to grab sub-pixel physical measurements natively
                        const p = prepareWithSegments(ch, font) as any;
                        // Support both pure pretext internal and standard canvas fallback
                        const width = (p.widths && p.widths.length > 0) ? p.widths[0] : (bCtx ? bCtx.measureText(ch).width : 8);
                        if (width <= 0) continue;
                        palette.push({ char: ch, weight, style, font, width, brightness: estimateBrightness(ch, font) });
                    } catch(e) { /* ignores */ }
                }
            }
        }

        const maxB = Math.max(...palette.map((p) => p.brightness));
        if (maxB > 0) for (const p of palette) p.brightness /= maxB;
        palette.sort((a, b) => a.brightness - b.brightness);
        
        const avgCharW = palette.reduce((s, p) => s + p.width, 0) / (palette.length || 1);
        const aspect = avgCharW / LINE_HEIGHT;
        const aspect2 = aspect * aspect;

        function findBest(targetB: number, targetW: number) {
            let lo = 0, hi = palette.length - 1;
            while (lo < hi) {
                const mid = (lo + hi) >> 1;
                if (palette[mid].brightness < targetB) lo = mid + 1;
                else hi = mid;
            }
            let bestScore = Infinity, best = palette[lo];
            for (let i = Math.max(0, lo - 15); i < Math.min(palette.length, lo + 15); i++) {
                const p = palette[i];
                const score = Math.abs(p.brightness - targetB) * 2.5 + Math.abs(p.width - targetW) / targetW;
                if (score < bestScore) {
                    bestScore = score;
                    best = p;
                }
            }
            return best;
        }

        function esc(c: string) {
            if (c === "&") return "&amp;";
            if (c === "<") return "&lt;";
            if (c === ">") return "&gt;";
            return c;
        }

        function wCls(w: number, s: string) {
            const wc = w === 300 ? "w3" : w === 500 ? "w5" : "w8";
            return s === "italic" ? wc + " it" : wc;
        }

        let COLS = 0;
        let ROWS = 0;
        const rowEls: HTMLDivElement[] = [];
        let density: Float32Array;
        let tempDen: Float32Array;

        // Smoke emitters: strong band along top + left edge so density matches bottom/right.
        const emitters = [
            { cx: 0.12, cy: 0.02, orbitR: 0.2, freq: 0.36, phase: 0.1, strength: 0.26 },
            { cx: 0.38, cy: 0.03, orbitR: 0.18, freq: 0.34, phase: 2.4, strength: 0.25 },
            { cx: 0.62, cy: 0.025, orbitR: 0.17, freq: 0.35, phase: 4.1, strength: 0.25 },
            { cx: 0.88, cy: 0.04, orbitR: 0.14, freq: 0.33, phase: 1.7, strength: 0.22 },
            { cx: 0.06, cy: 0.28, orbitR: 0.12, freq: 0.3, phase: 0.8, strength: 0.2 },
            { cx: 0.1, cy: 0.55, orbitR: 0.1, freq: 0.31, phase: 1.4, strength: 0.18 },
            { cx: 0.28, cy: 0.12, orbitR: 0.13, freq: 0.28, phase: 0.2, strength: 0.19 },
            { cx: 0.52, cy: 0.2, orbitR: 0.13, freq: 0.3, phase: 3.5, strength: 0.18 },
            { cx: 0.25, cy: 0.42, orbitR: 0.14, freq: 0.3, phase: 0, strength: 0.18 },
            { cx: 0.7, cy: 0.38, orbitR: 0.1, freq: 0.25, phase: 2.1, strength: 0.16 },
            { cx: 0.48, cy: 0.66, orbitR: 0.15, freq: 0.35, phase: 4.2, strength: 0.2 },
            { cx: 0.85, cy: 0.58, orbitR: 0.09, freq: 0.38, phase: 1.2, strength: 0.16 }
        ];

        function getVel(c: number, r: number, t: number) {
            const nx = c / COLS, ny = r / ROWS;
            let vx = Math.sin(ny * 6.28 + t * 0.3) * 2 + Math.cos((nx + ny) * 12.5 + t * 0.55) * 0.7 + Math.sin(nx * 25 + ny * 18 + t * 0.8) * 0.25;
            let vy = Math.cos(nx * 5 + t * 0.4) * 1.5 + Math.sin((nx - ny) * 10 + t * 0.4) * 0.8 + Math.cos(nx * 18 - ny * 25 + t * 0.7) * 0.25;
            vy *= aspect;
            /* Counteract downward drift so the top half of the panel stays populated. */
            vy -= 0.1 * (1 - ny);
            return [vx, vy];
        }

        function updateSim(t: number) {
            for (let r = 0; r < ROWS; r++) {
                for (let c = 0; c < COLS; c++) {
                    const [vx, vy] = getVel(c, r, t);
                    let sx = Math.max(0, Math.min(COLS - 1.001, c - vx));
                    let sy = Math.max(0, Math.min(ROWS - 1.001, r - vy));
                    const x0 = sx | 0, y0 = sy | 0;
                    const x1 = Math.min(x0 + 1, COLS - 1), y1 = Math.min(y0 + 1, ROWS - 1);
                    const fx = sx - x0, fy = sy - y0;
                    tempDen[r * COLS + c] = density[y0 * COLS + x0] * (1 - fx) * (1 - fy) + density[y0 * COLS + x1] * fx * (1 - fy) + density[y1 * COLS + x0] * (1 - fx) * fy + density[y1 * COLS + x1] * fx * fy;
                }
            }
            [density, tempDen] = [tempDen, density];
            for (let r = 1; r < ROWS - 1; r++) {
                for (let c = 1; c < COLS - 1; c++) {
                    const i = r * COLS + c;
                    const avg = (density[i - 1] + density[i + 1] + (density[i - COLS] + density[i + COLS]) * aspect2) / (2 + 2 * aspect2);
                    tempDen[i] = density[i] * 0.92 + avg * 0.08;
                }
            }
            [density, tempDen] = [tempDen, density];
            const spread = 4;
            for (const e of emitters) {
                const ex = (e.cx + Math.cos(t * e.freq + e.phase) * e.orbitR) * COLS;
                const ey = (e.cy + Math.sin(t * e.freq * 0.7 + e.phase) * e.orbitR * 0.8) * ROWS;
                const ec = ex | 0, er = ey | 0;
                for (let dr = -spread; dr <= spread; dr++) {
                    for (let dc = -spread; dc <= spread; dc++) {
                        const rr = er + dr, cc = ec + dc;
                        if (rr >= 0 && rr < ROWS && cc >= 0 && cc < COLS) {
                            const drScaled = dr / aspect;
                            const dist = Math.sqrt(drScaled * drScaled + dc * dc);
                            const s = Math.max(0, 1 - dist / (spread + 1));
                            density[rr * COLS + cc] = Math.min(1, density[rr * COLS + cc] + s * e.strength);
                        }
                    }
                }
            }
            for (let i = 0; i < COLS * ROWS; i++) density[i] *= 0.984;
        }

        function initGrid() {
            if (!container) return;
            const w = container.clientWidth;
            const h = container.clientHeight;
            if (w < 16 || h < LINE_HEIGHT) {
                return;
            }

            COLS = Math.max(1, Math.min(MAX_COLS, Math.floor(w / avgCharW)));
            ROWS = Math.max(1, Math.min(MAX_ROWS, Math.floor(h / LINE_HEIGHT)));
            density = new Float32Array(COLS * ROWS);
            tempDen = new Float32Array(COLS * ROWS);

            container.innerHTML = "";
            rowEls.length = 0;

            for (let r = 0; r < ROWS; r++) {
                const div = document.createElement("div");
                div.className = "fs-r";
                div.style.height = div.style.lineHeight = LINE_HEIGHT + "px";
                container.appendChild(div);
                rowEls.push(div);
            }
        }

        const handleResize = () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(initGrid, 150);
        };
        window.addEventListener("resize", handleResize);

        const ro =
            typeof ResizeObserver !== "undefined"
                ? new ResizeObserver(() => {
                      handleResize();
                  })
                : null;
        if (ro && smokeRoot) ro.observe(smokeRoot);

        initGrid();
        if (!COLS || !ROWS) {
            requestAnimationFrame(() => {
                initGrid();
            });
        }

        const targetCellW = () => (COLS > 0 ? container.clientWidth / COLS : 8);

        function renderLoop(now: number) {
            if (!container) return;
            if (!COLS || !ROWS || !density) {
                animationFrameId = requestAnimationFrame(renderLoop);
                return;
            }
            const t = now / 1000;
            updateSim(t);
            const tcw = targetCellW();

            for (let r = 0; r < ROWS; r++) {
                let html = "";
                for (let c = 0; c < COLS; c++) {
                    const b = density[r * COLS + c];
                    if (b < 0.025) {
                        html += " ";
                    } else {
                        const m = findBest(b, tcw);
                        const ai = Math.max(1, Math.min(10, Math.round(b * 10)));
                        html += `<span class="fs-w${m.weight === 300 ? 3 : m.weight === 500 ? 5 : 8}${m.style === 'italic' ? ' fs-it' : ''} fs-a${ai}">${esc(m.char)}</span>`;
                    }
                }
                if (rowEls[r]) {
                    rowEls[r].innerHTML = html;
                }
            }
            
            animationFrameId = requestAnimationFrame(renderLoop);
        }

        animationFrameId = requestAnimationFrame(renderLoop);

        return () => {
            cancelAnimationFrame(animationFrameId);
            window.removeEventListener("resize", handleResize);
            if (ro && smokeRoot) ro.unobserve(smokeRoot);
        };
    }, []);

    return (
        <div ref={smokeRootRef} className="login-fluid-smoke" style={{ overflow: "hidden" }}>
            {/* Matrix glyph ramp uses --fs-tint-rgb from Login.css (light vs dark accent) */}
            <style>
                {`
                    .fs-art-container {
                        box-sizing: border-box;
                        width: 100%;
                        min-width: 100%;
                        height: 100%;
                        min-height: 100%;
                    }
                    .fs-r {
                        white-space: pre;
                        overflow: hidden;
                        text-align: left;
                        box-sizing: border-box;
                        width: 100%;
                        font-family: var(--font-geek, "Fira Code", monospace); 
                        font-size: ${FONT_SIZE}px; 
                    }
                    .fs-w3 { font-weight: 300; } 
                    .fs-w5 { font-weight: 500; } 
                    .fs-w8 { font-weight: 800; }
                    .fs-it { font-style: italic; }
                    
                    .fs-a1 { color: rgba(var(--fs-tint-rgb), 0.08); } 
                    .fs-a2 { color: rgba(var(--fs-tint-rgb), 0.16); }
                    .fs-a3 { color: rgba(var(--fs-tint-rgb), 0.24); } 
                    .fs-a4 { color: rgba(var(--fs-tint-rgb), 0.32); }
                    .fs-a5 { color: rgba(var(--fs-tint-rgb), 0.42); } 
                    .fs-a6 { color: rgba(var(--fs-tint-rgb), 0.54); }
                    .fs-a7 { color: rgba(var(--fs-tint-rgb), 0.66); } 
                    .fs-a8 { color: rgba(var(--fs-tint-rgb), 0.78); }
                    .fs-a9 { color: rgba(var(--fs-tint-rgb), 0.90); } 
                    .fs-a10 { color: rgba(var(--fs-tint-rgb), 1); }
                `}
            </style>

            <div ref={containerRef} className="fs-art-container" />
        </div>
    );
}
